// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Findings are DERIVED from the data every build, never written by hand, and
// each one carries the rule that produced it so a reader can check the claim.
//
// Two constraints are load-bearing:
//   * No finding may rest on a preliminary year. The newest claims year is
//     revised upward every release, so "record high" on it is indefensible.
//   * No finding may rest on a suppressed (NP) cell.

import { change, lastKnown } from './format';
import { divisions, subdivisions, totalOf, withDivision } from './data';
import type { Bundle, ClaimRow, Val } from './types';

export interface Finding {
  id: string;
  severity: 'info' | 'warn' | 'alert';
  title: string;
  body: string;
  rule: string;
  view?: string;
}

const finite = (v: Val): v is number => v !== null && Number.isFinite(v);

function fmtInt(n: number): string { return Math.round(n).toLocaleString('en-AU'); }
function fmtMoney(n: number): string { return `$${Math.round(n).toLocaleString('en-AU')}`; }

/** Index of the last period at which `key` is published for `row`. */
function lastIdx(row: ClaimRow, key: 'count' | 'comp' | 'lost' | 'freq'): number {
  const series = row[key];
  if (!series) return -1;
  return lastKnown(series)?.index ?? -1;
}

export function findings(b: Bundle): Finding[] {
  const out: Finding[] = [];
  const { claims, fatalities, enforcement } = b;
  const P = claims.periods;
  const n = P.length;

  const total = totalOf(claims.nature);
  const mental = claims.nature.find((r) => r.code === 'I');

  // 1. The psychological-injury crossover — the site's spine.
  if (total && mental && finite(total.count[0]) && finite(mental.count[0]) && finite(total.count[n - 1]) && finite(mental.count[n - 1])) {
    const growthAll = change(total.count[0], total.count[n - 1]);
    const growthMh = change(mental.count[0], mental.count[n - 1]);
    const shareFrom = (mental.count[0]! / total.count[0]!) * 100;
    const shareTo = (mental.count[n - 1]! / total.count[n - 1]!) * 100;
    if (growthAll !== null && growthMh !== null && growthMh > growthAll * 2) {
      out.push({
        id: 'psych-growth',
        severity: 'alert',
        title: 'Psychological claims are growing four times faster than everything else',
        body: `Serious mental health condition claims rose from ${fmtInt(mental.count[0]!)} in ${P[0]} to ${fmtInt(mental.count[n - 1]!)} in ${P[n - 1]}, up ${growthMh.toFixed(1)}%, while serious claims overall grew ${growthAll.toFixed(1)}%. They went from ${shareFrom.toFixed(2)}% of all serious claims to ${shareTo.toFixed(2)}% — roughly one claim in eight.`,
        rule: 'Nature-of-injury group I against the published Total, first vs last published year.',
        view: 'psych',
      });
    }
  }

  // 2. The cost asymmetry, measured on the last year both medians are published
  //    (never the preliminary year, where both are suppressed outright).
  if (total && mental) {
    const i = Math.min(lastIdx(mental, 'comp'), lastIdx(total, 'comp'));
    const j = Math.min(lastIdx(mental, 'lost'), lastIdx(total, 'lost'));
    if (i >= 0 && finite(mental.comp![i]) && finite(total.comp![i])) {
      const rc = mental.comp![i]! / total.comp![i]!;
      const rl = j >= 0 && finite(mental.lost![j]) && finite(total.lost![j]) ? mental.lost![j]! / total.lost![j]! : null;
      out.push({
        id: 'psych-cost',
        severity: 'alert',
        title: 'A psychological claim costs four times the typical claim',
        body: `In ${P[i]} the median mental health claim paid ${fmtMoney(mental.comp![i]!)} against ${fmtMoney(total.comp![i]!)} for the median claim of any kind — ${rc.toFixed(2)} times as much.${rl ? ` The median time off work was ${mental.lost![j]!.toFixed(1)} working weeks against ${total.lost![j]!.toFixed(1)} — ${rl.toFixed(2)} times as long.` : ''} ${P[n - 1]} is withheld on both measures because compensation is still accruing.`,
        rule: `Median compensation and median time lost, mental health conditions vs Total, at the most recent non-suppressed year (${P[i]}).`,
        view: 'psych',
      });
    }
  }

  // 3. Deaths against injuries — the two curves moving in opposite directions.
  const series = fatalities.series;
  if (series.length > 4) {
    const first = series[0];
    const recent = series[series.length - 1];
    const rateFrom = series.find((d) => finite(d.rate));
    if (rateFrom && finite(recent.rate)) {
      const drop = ((rateFrom.rate! - recent.rate!) / rateFrom.rate!) * 100;
      out.push({
        id: 'fatal-trend',
        severity: 'info',
        title: 'Work deaths have roughly halved per worker since 2003',
        body: `${fmtInt(first.count)} workers were killed by injuries at work in ${first.year}, a rate of ${rateFrom.rate!.toFixed(1)} per 100,000 workers. In ${recent.year} it was ${fmtInt(recent.count)} at ${recent.rate!.toFixed(2)} — a ${drop.toFixed(0)}% fall in the rate. Deaths from occupational disease are counted separately and are undercounted everywhere.`,
        rule: 'Fatality rate per 100,000 workers, first published year vs latest, across the stitched 2003–2024 series.',
        view: 'deaths',
      });
    }
  }

  // 4. Which industry hurts people most often, on the rate rather than the count
  //    (the count just re-ranks industries by how many people work in them).
  const subs = withDivision(claims.industry).filter((r) => r.freq);
  const freqIdx = subs.length ? lastKnown(subs[0].freq!)?.index ?? n - 1 : n - 1;
  const byFreq = subs
    .filter((r) => finite(r.freq![freqIdx]) && finite(r.count[freqIdx]) && r.count[freqIdx]! >= 200)
    .sort((a, b) => b.freq![freqIdx]! - a.freq![freqIdx]!);
  if (byFreq.length > 1) {
    const top = byFreq[0];
    const median = byFreq[Math.floor(byFreq.length / 2)];
    out.push({
      id: 'most-frequent',
      severity: 'warn',
      title: `${top.name} has the highest injury rate of any industry`,
      body: `${top.name} recorded ${top.freq![freqIdx]!.toFixed(1)} serious claims per million hours worked in ${P[freqIdx]}, against ${median.freq![freqIdx]!.toFixed(1)} for the median industry — ${(top.freq![freqIdx]! / median.freq![freqIdx]!).toFixed(1)} times the middle of the pack. It sits in ${top.divisionName}.`,
      rule: 'Highest frequency rate among ANZSIC subdivisions with at least 200 claims in the year, compared with the median subdivision.',
      view: 'cost',
    });
  }

  // 5. The quadrant finding: frequent-and-cheap vs rare-and-ruinous.
  const compIdx = subs.length ? Math.max(0, lastKnown(subs.find((r) => r.comp)?.comp ?? [])?.index ?? n - 2) : n - 2;
  const quad = subs.filter((r) => finite(r.freq?.[compIdx] ?? null) && finite(r.comp?.[compIdx] ?? null) && finite(r.count[compIdx]) && r.count[compIdx]! >= 200);
  if (quad.length > 6) {
    const medFreq = median(quad.map((r) => r.freq![compIdx]!));
    const medComp = median(quad.map((r) => r.comp![compIdx]!));
    const rareRuinous = quad.filter((r) => r.freq![compIdx]! < medFreq && r.comp![compIdx]! > medComp).sort((a, b) => b.comp![compIdx]! - a.comp![compIdx]!);
    if (rareRuinous.length) {
      const t = rareRuinous[0];
      out.push({
        id: 'rare-ruinous',
        severity: 'info',
        title: 'The most dangerous industries are not the most expensive ones',
        body: `${t.name} injures people less often than the median industry (${t.freq![compIdx]!.toFixed(1)} against ${medFreq.toFixed(1)} claims per million hours) but each claim costs far more (${fmtMoney(t.comp![compIdx]!)} against ${fmtMoney(medComp)}). ${rareRuinous.length} industries sit in that rare-but-ruinous quadrant, and safety spending aimed only at claim counts misses all of them.`,
        rule: `Subdivisions below the median frequency rate and above the median compensation in ${P[compIdx]}, minimum 200 claims.`,
        view: 'cost',
      });
    }
  }

  // 6. Body stressing dominance.
  const mech = claims.mechanism.filter((r) => r.level === 1 && !r.total);
  const mechIdx = n - 1;
  const mechRanked = mech.filter((r) => finite(r.count[mechIdx])).sort((a, b) => b.count[mechIdx]! - a.count[mechIdx]!);
  const mechTotal = mechRanked.reduce((a, r) => a + r.count[mechIdx]!, 0);
  if (mechRanked.length > 1 && mechTotal > 0) {
    const top = mechRanked[0];
    out.push({
      id: 'mechanism-top',
      severity: 'info',
      title: `${top.name} causes more serious injuries than any other mechanism`,
      body: `${fmtInt(top.count[mechIdx]!)} serious claims in ${P[mechIdx]} — ${((top.count[mechIdx]! / mechTotal) * 100).toFixed(1)}% of all of them, and ${(top.count[mechIdx]! / mechRanked[1].count[mechIdx]!).toFixed(1)} times the next mechanism (${mechRanked[1].name.toLowerCase()}).`,
      rule: 'Largest top-level TOOCS mechanism of incident by claim count in the latest year.',
      view: 'injuries',
    });
  }

  // 7. Enforcement effort spread. Inspector density varies several-fold, which
  //    is the enforcement view's whole reason to exist.
  const insp = enforcement.inspectorsPer10k;
  if (insp?.rows?.length) {
    const i = insp.periods.length - 1;
    const states = insp.rows.filter((r) => ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'].includes(r.j) && finite(r.v[i]));
    if (states.length > 2) {
      const sorted = [...states].sort((a, b) => b.v[i]! - a.v[i]!);
      const hi = sorted[0];
      const lo = sorted[sorted.length - 1];
      out.push({
        id: 'inspector-density',
        severity: 'warn',
        title: 'Your chance of ever seeing a safety inspector depends on which state you work in',
        body: `${hi.j} fields ${hi.v[i]!.toFixed(2)} active inspectors per 10,000 employees and ${lo.j} fields ${lo.v[i]!.toFixed(2)} — ${(hi.v[i]! / lo.v[i]!).toFixed(1)} times as many, for workers covered by broadly the same model laws.`,
        rule: 'Highest vs lowest field-active inspectors per 10,000 employees among the eight states and territories, latest year.',
        view: 'enforcement',
      });
    }
  }

  // 8. Fines concentration.
  const fines = enforcement.fines;
  if (fines?.rows?.length) {
    const i = fines.periods.length - 1;
    const rows = fines.rows.filter((r) => r.j !== 'AUST' && finite(r.v[i]));
    const natl = fines.rows.find((r) => r.j === 'AUST');
    if (rows.length > 2 && natl && finite(natl.v[i]) && natl.v[i]! > 0) {
      const top = [...rows].sort((a, b) => b.v[i]! - a.v[i]!)[0];
      out.push({
        id: 'fines',
        severity: 'info',
        title: 'Court-ordered safety fines are concentrated in one jurisdiction',
        body: `Australian courts ordered ${fmtMoney(natl.v[i]!)} in work health and safety fines in ${fines.periods[i]}. ${top.j} accounts for ${fmtMoney(top.v[i]!)} of it — ${((top.v[i]! / natl.v[i]!) * 100).toFixed(0)}% of the national total.`,
        rule: 'Largest jurisdiction share of total court-ordered fines (Indicator 12d_c), latest year.',
        view: 'enforcement',
      });
    }
  }

  // 9. Older workers lose the most time.
  const demo = claims.demographics.filter((r) => r.level === 1 && !r.total);
  const lostIdx = lastKnown(demo.find((r) => r.lost)?.lost ?? [])?.index ?? -1;
  if (lostIdx >= 0) {
    const withLost = demo.filter((r) => finite(r.lost?.[lostIdx] ?? null));
    if (withLost.length > 2) {
      const sorted = [...withLost].sort((a, b) => b.lost![lostIdx]! - a.lost![lostIdx]!);
      const hi = sorted[0];
      const lo = sorted[sorted.length - 1];
      out.push({
        id: 'age-time',
        severity: 'info',
        title: 'Older workers take far longer to get back to work',
        body: `Injured workers aged ${hi.name.toLowerCase()} lose a median ${hi.lost![lostIdx]!.toFixed(1)} working weeks, against ${lo.lost![lostIdx]!.toFixed(1)} for those aged ${lo.name.toLowerCase()} — ${(hi.lost![lostIdx]! / lo.lost![lostIdx]!).toFixed(1)} times as long, in ${P[lostIdx]}.`,
        rule: 'Highest vs lowest median time lost by age group at the most recent non-suppressed year.',
        view: 'injuries',
      });
    }
  }

  // 10. The revision warning — an honesty finding, not a data finding.
  if (fatalities.revisions.length) {
    const worst = [...fatalities.revisions].sort((a, b) => Math.abs(b.current - b.archive) - Math.abs(a.current - a.archive))[0];
    out.push({
      id: 'revisions',
      severity: 'warn',
      title: 'Published death counts move for years after the year ends',
      body: `${fatalities.revisions.length} of the years both Safe Work Australia releases cover have been revised. ${worst.year} was first published as ${worst.archive} deaths and now stands at ${worst.current}, as coronial investigations concluded. This site takes every shared year from the current release and marks where the older series stops.`,
      rule: 'Years where the 2021-vintage time series and the current detailed file disagree.',
      view: 'deaths',
    });
  }

  return out;
}

function median(values: number[]): number {
  const s = [...values].sort((a, b) => a - b);
  if (!s.length) return 0;
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export { median, divisions, subdivisions };
